import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { isBefore, isAfter } from 'date-fns'

import { Button } from 'components/UIElements'
import DayPickerInput from 'components/DayPickerInput'
import { createMomentObject, dayPickerConfig } from 'utils/time'

const PastDates = ({ classNames, children, selectedDay, selectedRange, onResetDates, onAllPastDates, ...props }) => {
  return (
    <div className={classNames.overlayWrapper} {...props}>
      <div className={`${classNames.overlay} past-overlay`}>
        {children}
        <div className='flexed column'>
          {selectedDay[1]?.to && <Button noStyling className='past' content='All past dates' onClick={onAllPastDates} />}
          <Button noStyling className='all-dates' content='Allow all dates' onClick={onResetDates} />
        </div>
      </div>
    </div>
  )
}

const FutureDates = ({ classNames, children, selectedDay, selectedRange, onResetDates, onAllFutureDates, ...props }) => {
  return (
    <div className={classNames.overlayWrapper} {...props}>
      <div className={classNames.overlay}>
        {children}
        <div className='flexed column'>
          {selectedDay[0] && (
            <Button noStyling className='future' content='All future dates' onClick={onAllFutureDates} />
          )}
          <Button noStyling className='all-dates' content='Allow all dates' onClick={onResetDates} />
        </div>
      </div>
    </div>
  )
}

const DatePickerRange = ({
  disabled,
  isLastQuestion,
  item,
  onAllFutureDates,
  onAllPastDates,
  onFromDateChange,
  onToDateChange,
  onResetDates,
}) => {
  const [openInput, setOpenInput] = useState('')
  const toPickerRef = useRef()
  const { min, max } = item.input_validation.absolute_date
  const isButtonVisible = !!(max && min) || (openInput === 'to' && !!max) || (openInput === 'from' && !!min)

  // min and max need to be converted into Date objects again when a date or datetime question is duplicated
  const minDate = min ? createMomentObject(min).toDate() : ''
  const maxDate = max ? createMomentObject(max).toDate() : ''

  const isPastDate = day => {
    if (!max || (max && min)) return false
    return isBefore(day, maxDate)
  }
  const isFutureDate = day => {
    if (!min || (max && min)) return false
    return isAfter(day, minDate)
  }

  const modifiers = { start: minDate, end: maxDate, isPastDate, isFutureDate }

  const addProps = (Component, isFuture) => {
    const WrappedComponent = props => {
      return isFuture ? (
        <Component {...props} onResetDates={onResetDates} onAllFutureDates={onAllFutureDates} />
      ) : (
        <Component {...props} onResetDates={onResetDates} onAllPastDates={onAllPastDates} />
      )
    }
    return WrappedComponent
  }

  const commonProps = {
    format: dayPickerConfig.formatDateFns,
    numberOfMonths: 2,
    dayPickerProps: {
      numberOfMonths: 2,
      modifiers,
      locale: dayPickerConfig.locale,
      selectedRange: { from: minDate, to: maxDate },
    },
  }

  return (
    <div
      className={`date-picker flexed ${isLastQuestion ? 'last-question' : ''} ${isButtonVisible ? 'with-button' : ''} ${disabled ? 'disabled' : ''
        }`}>
      <DayPickerInput
        {...commonProps}
        disabled={disabled}
        dayPickerProps={{
          ...commonProps.dayPickerProps,
          disabled: { after: maxDate },
          endMonth: maxDate ? new Date(maxDate) : undefined,
          onSelect: () => toPickerRef.current?.getInput?.()?.focus(),
        }}
        onDayChange={day => onFromDateChange(day)}
        overlayComponent={addProps(PastDates)}
        placeholder='All Past Dates'
        value={minDate}
      />
      {` — `}
      <span className='date-picker-to'>
        <DayPickerInput
          {...commonProps}
          ref={toPickerRef}
          disabled={disabled}
          dayPickerProps={{
            ...commonProps.dayPickerProps,
            disabled: { before: minDate },
            startMonth: minDate ? new Date(minDate) : undefined,
          }}
          overlayComponent={addProps(FutureDates, true)}
          onDayChange={day => onToDateChange(day)}
          placeholder='All Future Dates'
          value={maxDate}
        />
      </span>
    </div>
  )
}

DatePickerRange.propTypes = {
  disabled: PropTypes.bool,
  isLastQuestion: PropTypes.bool,
  item: PropTypes.object,
  onFromDateChange: PropTypes.func,
  onToDateChange: PropTypes.func,
  onAllFutureDates: PropTypes.func,
  onAllPastDates: PropTypes.func,
  onResetDates: PropTypes.func,
}
PastDates.propTypes = {
  children: PropTypes.node,
  classNames: PropTypes.objectOf(PropTypes.string),
  onAllFutureDates: PropTypes.func,
  onAllPastDates: PropTypes.func,
  onResetDates: PropTypes.func,
}
FutureDates.propTypes = PastDates.propTypes

export default DatePickerRange
